import { useRef, useState, useCallback, UIEvent } from 'react';

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  Table as ReactTable,
  ColumnDef,
  Row,
} from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';

interface TableProps<T> {
  readonly data: T[];
  readonly columns: ColumnDef<T>[];
  readonly highlightedRowIndexes?: number[];
  readonly bordered?: boolean;
  readonly rounded?: boolean;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly hideHeader?: boolean;
  readonly onBottomReached?: (parentElement: HTMLTableSectionElement) => void;
  readonly rowOnClick?: (row: Row<T>) => void;
  readonly className?: string;
}

const SIZE_STYLE = {
  sm: 'py-[14px] text-sm',
  md: 'py-[14px]',
  lg: 'py-[18px]',
};

const defaults: unknown[] = [];

function Table<T>({
  data,
  columns,
  highlightedRowIndexes = defaults as number[],
  bordered,
  rounded,
  hideHeader,
  size = 'md',
  onBottomReached,
  rowOnClick,
  className,
}: TableProps<T>) {
  const [selectedRowId, setSelectedRowId] = useState<string>();
  const tableContainerRef = useRef<HTMLTableElement>(null);
  const tableConfig: ReactTable<T> = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: 150,
    },
    // Pipeline
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const { rows } = tableConfig.getRowModel();

  const onRowClick = useCallback(
    (row: Row<T>) => {
      setSelectedRowId(row.id);
      rowOnClick?.(row);
    },
    [rowOnClick],
  );

  const onScroll = useCallback(
    (e: UIEvent<HTMLTableSectionElement>) => {
      onBottomReached?.(e.currentTarget);
    },
    [onBottomReached],
  );

  return (
    <table className={twMerge('h-full w-full min-w-full border-collapse border-spacing-0 bg-white', className)} ref={tableContainerRef}>
      <thead
        aria-hidden={hideHeader}
        className={twMerge(
          'block bg-neutral-100',
          'hover:bg-neutral-200',
          rounded && 'rounded-t-xl',
          bordered && 'border-b border-neutral-200',
          hideHeader && 'hidden',
        )}
      >
        {tableConfig.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="flex">
            {headerGroup.headers.map((header) => {
              return (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  className={twMerge('grow px-4 text-left font-semibold text-neutral-700', SIZE_STYLE[size])}
                  style={{ width: header.column.getSize() }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody className={twMerge('block', rowOnClick && 'cursor-pointer', bordered && 'border-b border-neutral-200')} onScroll={onScroll}>
        {rows.map((row) => {
          return (
            <tr
              data-row-selected={selectedRowId === row.id}
              key={row.id}
              className={twMerge(
                'flex items-center text-neutral-700',
                bordered && 'border-b border-neutral-200',
                !highlightedRowIndexes?.includes(row.index) ? ' hover:bg-neutral-50' : 'bg-light-blue-50 hover:bg-light-blue-100',
              )}
              onClick={() => onRowClick(row)}
            >
              {row.getVisibleCells().map((cell) => {
                return (
                  <td
                    key={cell.id}
                    className={twMerge('grow truncate px-4 text-left', SIZE_STYLE[size])}
                    style={{ width: cell.column.getSize() }}
                    role="gridcell"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr className="flex w-full justify-center py-4 text-sm font-medium">
            <td>No Data Found</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export { Table };
